import React, { useState, useEffect } from 'react';
import { Link as ScrollLink } from 'react-scroll';

const Header = () => {
  const [language, setLanguage] = useState('Español');
  const [isOpen, setIsOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('');
  const [isSticky, setIsSticky] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    { name: 'Inicio', to: 'inicio' },
    { name: 'Sobre mi', to: 'sobre-mi' },
    { name: 'Tecnologías', to: 'tecnologias' },
    { name: 'Apps', to: 'apps-usadas' },
    //{ name: 'Experiencia', to: 'experiencia' },
    { name: 'En progreso', to: 'trabajo-en-progreso' },
    { name: 'Proyectos', to: 'proyectos' },
    { name: 'Contacto', to: 'contacto' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cerrar menú móvil al cambiar tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isSticky 
          ? 'py-0' 
          : 'bg-custom-bg py-0'
      }`}
    >
      {/* Contenedor principal que se expande/contrae */}
      <div className={`mx-auto transition-all duration-500 ${
        isSticky 
          ? 'max-w-3xl rounded-xl mt-2 shadow-lg' 
          : 'max-w-full'
      }`}>
        
        {/* Contenedor interno con padding variable */}
        <div className={`flex justify-between items-center transition-all duration-300 ${
          isSticky 
            ? 'px-6 py-3 bg-custom-bg/80 backdrop-blur-sm rounded-xl' 
            : 'px-4 md:px-8 py-4'
        }`}>
          
          {/* Logo con efecto de reducción */}
          <div className="flex items-center">
            <img 
              src="/images/navico.png" 
              alt="Logo" 
              className={`transition-all duration-300 ${
                isSticky ? 'w-8 h-8' : 'w-10 h-10'
              }`} 
            />
          </div>
          
          {/* Menú desktop (oculto en móvil) */}
          <nav className="hidden md:flex space-x-1">
            {menuItems.map((item) => (
              <ScrollLink
                key={item.name}
                to={item.to}
                smooth={true}
                duration={500}
                offset={-70}
                className={`text-gray-300 hover:text-pastel-yellow relative cursor-pointer px-3 py-2 transition-all duration-200 ${
                  activeLink === item.name ? 'text-pastel-yellow' : ''
                } ${
                  isSticky ? 'text-sm' : 'text-base'
                }`}
                onMouseEnter={() => setActiveLink(item.name)}
                onMouseLeave={() => setActiveLink('')}
                onSetActive={() => setActiveLink(item.name)}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
                <span
                  className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/4 h-0.5 bg-pastel-yellow transition-all duration-300 ${
                    activeLink === item.name ? 'scale-x-100' : 'scale-x-0'
                  }`}
                ></span>
              </ScrollLink>
            ))}
          </nav>
          
          {/* Menú móvil */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-300 focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
          
          {/* Selector de idioma (solo desktop) */}
          <div className="hidden md:block relative">
            <button
              className="hidden text-gray-300 bg-gray-600 px-4 py-1 rounded-md transition-all duration-300"
              onClick={() => setIsOpen(!isOpen)}
            >
              {language}
            </button>
            {isOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-custom-bg border border-gray-600 rounded-md shadow-lg z-10">
                <ul className="py-1">
                  <li>
                    <button
                      onClick={() => { setLanguage('English'); setIsOpen(false); }}
                      className="block px-4 py-2 text-gray-300 hover:bg-gray-700 w-full text-left"
                    >
                      English
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => { setLanguage('Español'); setIsOpen(false); }}
                      className="block px-4 py-2 text-gray-300 hover:bg-gray-700 w-full text-left"
                    >
                      Español
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => { setLanguage('Korean'); setIsOpen(false); }}
                      className="block px-4 py-2 text-gray-300 hover:bg-gray-700 w-full text-left"
                    >
                      Korean
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
        
        {/* Menú móvil desplegable */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-custom-bg/95 backdrop-blur-sm px-4 py-3">
            <div className="flex flex-col space-y-3">
              {menuItems.map((item) => (
                <ScrollLink
                  key={item.name}
                  to={item.to}
                  smooth={true}
                  duration={500}
                  offset={-70}
                  className={`text-gray-300 hover:text-pastel-yellow relative cursor-pointer py-2 px-4 rounded-md transition-all ${
                    activeLink === item.name ? 'bg-gray-700 text-pastel-yellow' : ''
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  onSetActive={() => setActiveLink(item.name)}
                >
                  {item.name}
                </ScrollLink>
              ))}
              
              {/* Selector de idioma para móvil */}
              <div className="hidden pt-3 border-t border-gray-700">
                <select 
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full bg-gray-700 text-gray-300 rounded-md p-2"
                >
                  <option value="English">English</option>
                  <option value="Español">Español</option>
                  <option value="Korean">Korean</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;